import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from "react";
import { Loader2 } from "lucide-react";

/* ── Button ─────────────────────────────────────────────────────────────── */
type Variant = "primary" | "outline" | "ghost";
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  loading?: boolean;
}

const variants: Record<Variant, string> = {
  primary:
    "bg-gradient-to-b from-gold-300 to-gold text-ink-900 font-medium shadow-[0_8px_30px_-12px_rgba(201,162,39,0.6)] hover:from-gold-300 hover:to-gold-400 hover:shadow-[0_10px_36px_-10px_rgba(201,162,39,0.75)]",
  outline:
    "border border-slate-600/50 text-slate-200 hover:border-gold/60 hover:text-white hover:bg-white/[0.02]",
  ghost: "text-slate-400 hover:text-white hover:bg-white/[0.04]",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", loading, className = "", children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/50 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900 disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${className}`}
      {...rest}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
});

/* ── Field (etiketli input + hata) ──────────────────────────────────────── */
interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, error, hint, className = "", id, ...rest },
  ref,
) {
  const fieldId = id ?? rest.name;
  return (
    <div className="space-y-1.5">
      <label htmlFor={fieldId} className="block text-sm font-medium text-slate-300">
        {label}
      </label>
      <input
        ref={ref}
        id={fieldId}
        className={`w-full rounded-lg border bg-ink-800/60 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 transition-colors focus:outline-none focus:ring-2 focus:ring-gold/40 ${
          error ? "border-red-500/60" : "border-slate-700/60 focus:border-gold/50"
        } ${className}`}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      {error ? (
        <p className="text-xs text-red-400">{error}</p>
      ) : hint ? (
        <p className="text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
});

/* ── Spinner (tam ekran yükleme) ────────────────────────────────────────── */
export function FullPageSpinner({ label }: { label?: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3">
      <Loader2 className="h-7 w-7 animate-spin text-gold" />
      {label && <p className="text-sm text-slate-500">{label}</p>}
    </div>
  );
}

/* ── Card ───────────────────────────────────────────────────────────────── */
export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-2xl border border-slate-700/40 bg-ink-800/50 backdrop-blur-sm ${className}`}
    >
      {children}
    </div>
  );
}
